import { Component, computed, DestroyRef, inject, OnInit, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { SelectLocationListItemComponent } from '@app/shared/components/select-location-list-item/select-location-list-item.component';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { LocationsDataService } from '@app/shared/services/locations-data.service';
import { ChangeLocationParentDialogData, Location } from '@app/shared/types/models/location/location.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs';

@Component({
  selector: 'proffeo-change-location-parent-dialog',
  imports: [
    SelectLocationListItemComponent,
    MatDialogModule,
    MatRadioModule,
    TranslatePipe,
    UiDialogComponent,
    UiButtonComponent
  ],
  templateUrl: './change-location-parent-dialog.component.html'
})
export class ChangeLocationParentDialogComponent implements OnInit {
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly locationsDataService: LocationsDataService = inject(LocationsDataService);
  private readonly dialogRef: MatDialogRef<ChangeLocationParentDialogComponent> = inject(
    MatDialogRef<ChangeLocationParentDialogComponent>
  );
  private readonly dialogData: ChangeLocationParentDialogData = inject(MAT_DIALOG_DATA);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  protected readonly isSaving = signal(false);
  protected readonly selectedNewParent = signal<Location | undefined>(undefined);
  protected readonly isRootParentSelected = computed(() => this.selectedNewParent() === undefined);

  protected location: Location;
  protected availableLocations: Signal<Location[]>;
  protected excludedParentLocationIds = signal<Set<string>>(new Set());
  protected selectedProject: Signal<Project> = this.activeProjectService.activeProject;

  public ngOnInit(): void {
    this.availableLocations = this.dialogData.locations;
    this.location = this.dialogData.location;
    const parentLocation = this.location.parentLocation?.();
    this.selectedNewParent.set(parentLocation?.id ? parentLocation : undefined);

    const locationToMoveId = this.dialogData.location.id;
    const subLocationIds = this.locationsDataService.getAllSubLocations(locationToMoveId).map(loc => loc.id);
    this.excludedParentLocationIds.set(new Set([locationToMoveId, ...subLocationIds]));
  }

  protected pickProjectRootAsParent(): void {
    this.selectedNewParent.set(undefined);
  }

  protected pickLocationAsParent(location: Location): void {
    this.selectedNewParent.set(location);
  }

  public save(): void {
    if (this.isSaving()) {
      return;
    }
    this.isSaving.set(true);
    this.locationsDataService
      .updateLocation(
        this.selectedProject()?.id,
        this.location.id,
        this.location.name,
        this.location.orderNo,
        this.selectedNewParent()?.id
      )
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.dialogRef.close());
  }
}
